/**
 * @sazeo/sdk — CRM-Leads hooks
 *
 * TanStack Query wrappers for the crm-leads addon. All calls route through
 * the Edge Function at `/addons/crm-leads/*` which is guarded by
 * requireAddonEnabled() and enforces RLS via the caller's JWT.
 *
 * Paired spec: specs/CODEX_PHASE4_WAVE2_CRM_INVOICING.md §2.4
 */

import type {
  CrmLead,
  CrmLeadActivity,
  CrmLeadActivityCreateInput,
  CrmLeadCreateInput,
  CrmLeadFilter,
  CrmLeadTransitionInput,
  CrmLeadUpdateInput,
  CrmPipeline,
  CrmStage,
} from '@sazeo/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const crmKeys = {
  all: ['crm-leads'] as const,
  pipelines: () => [...crmKeys.all, 'pipelines'] as const,
  stages: (pipelineId?: string) => [...crmKeys.all, 'stages', pipelineId ?? 'all'] as const,
  leads: (filter?: CrmLeadFilter) => [...crmKeys.all, 'leads', filter ?? null] as const,
  lead: (id: string) => [...crmKeys.all, 'lead', id] as const,
  leadActivities: (leadId: string) => [...crmKeys.all, 'lead', leadId, 'activities'] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers (mirror sdk/addons.ts response handling)
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  // 204 No Content → null
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

function buildQuery(filter?: CrmLeadFilter): string {
  if (!filter) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v === undefined || v === null) continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ---------------------------------------------------------------------------
// Pipelines + Stages
// ---------------------------------------------------------------------------

export function usePipelines(supabase: SupabaseClientLike) {
  return useQuery({
    queryKey: crmKeys.pipelines(),
    queryFn: () => apiFetch<CrmPipeline[]>(supabase, 'addons/crm-leads/pipelines'),
  });
}

export function useStages(supabase: SupabaseClientLike, pipelineId?: string) {
  return useQuery({
    queryKey: crmKeys.stages(pipelineId),
    queryFn: () =>
      apiFetch<CrmStage[]>(
        supabase,
        `addons/crm-leads/stages${pipelineId ? `?pipeline_id=${pipelineId}` : ''}`,
      ),
    enabled: true,
  });
}

// ---------------------------------------------------------------------------
// Leads
// ---------------------------------------------------------------------------

export function useLeads(supabase: SupabaseClientLike, filter?: CrmLeadFilter) {
  return useQuery({
    queryKey: crmKeys.leads(filter),
    queryFn: () => apiFetch<CrmLead[]>(supabase, `addons/crm-leads/leads${buildQuery(filter)}`),
  });
}

export function useLead(supabase: SupabaseClientLike, id: string) {
  return useQuery({
    queryKey: crmKeys.lead(id),
    queryFn: () => apiFetch<CrmLead>(supabase, `addons/crm-leads/leads/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateLead(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CrmLeadCreateInput) =>
      apiFetch<CrmLead>(supabase, 'addons/crm-leads/leads', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: crmKeys.all });
    },
  });
}

export function useUpdateLead(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: CrmLeadUpdateInput }) =>
      apiFetch<CrmLead>(supabase, `addons/crm-leads/leads/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: crmKeys.lead(vars.id) });
      void qc.invalidateQueries({ queryKey: crmKeys.all });
    },
  });
}

export function useDeleteLead(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(supabase, `addons/crm-leads/leads/${id}`, { method: 'DELETE' }),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: crmKeys.lead(id) });
      void qc.invalidateQueries({ queryKey: crmKeys.all });
    },
  });
}

export function useTransitionLead(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: CrmLeadTransitionInput;
    }) =>
      apiFetch<CrmLead>(supabase, `addons/crm-leads/leads/${id}/transition`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: crmKeys.lead(vars.id) });
      void qc.invalidateQueries({ queryKey: crmKeys.leadActivities(vars.id) });
      void qc.invalidateQueries({ queryKey: crmKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Activities
// ---------------------------------------------------------------------------

export function useLeadActivities(supabase: SupabaseClientLike, leadId: string) {
  return useQuery({
    queryKey: crmKeys.leadActivities(leadId),
    queryFn: () =>
      apiFetch<CrmLeadActivity[]>(supabase, `addons/crm-leads/leads/${leadId}/activities`),
    enabled: Boolean(leadId),
  });
}

export function useAddLeadActivity(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      leadId,
      input,
    }: {
      leadId: string;
      input: CrmLeadActivityCreateInput;
    }) =>
      apiFetch<CrmLeadActivity>(supabase, `addons/crm-leads/leads/${leadId}/activities`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: crmKeys.leadActivities(vars.leadId) });
    },
  });
}
